import type { FunctionComponent, PropsWithChildren } from 'react';
import React from 'react';
import { unstable_batchedUpdates } from 'react-dom';

export type CustomObserver = {
  disconnect: () => void;
  observe: (target: Element, setIsVisible: (visible: boolean) => void) => void;
  unobserve: (target: Element) => void;
};

export const IntersectionObserverBox: FunctionComponent<
  PropsWithChildren<{
    observer: CustomObserver | undefined;
    placeholder: React.ReactNode | null;
  }>
> = (props) => {
  const itemRef = React.useRef<Element | null | undefined>();
  const [visible, setIsVisible] = React.useState(false);
  const { children, observer, placeholder = null, ...restProps } = props;

  return (
    <div
      {...restProps}
      ref={(ref) => {
        if (ref) {
          itemRef.current = ref;
          observer?.observe(ref, setIsVisible);
        } else if (itemRef.current) {
          observer?.unobserve(itemRef.current);
          itemRef.current = null;
        }
      }}
    >
      {visible ? children : placeholder}
    </div>
  );
};

export function prepareIntersectionObserver(
  observer: CustomObserver | undefined,
  ref: Element,
  setObserver: (value: CustomObserver) => void,
) {
  if (observer) {
    observer.disconnect();
  }
  const targetToSetter = new Map<Element, (visible: boolean) => void>();
  const nextObserver = new IntersectionObserver(
    (entries) => {
      unstable_batchedUpdates(() => {
        entries.forEach((entry) => {
          if (entry) {
            targetToSetter.get(entry.target)?.(entry.isIntersecting);
          }
        });
      });
    },
    {
      root: ref,
      rootMargin: '10% 100% 10% 100%',
    },
  );
  setObserver({
    observe: (target: Element, setIsVisible: (visible: boolean) => void) => {
      nextObserver.observe(target);
      targetToSetter.set(target, setIsVisible);
    },
    unobserve: (target: Element) => {
      nextObserver.unobserve(target);
      targetToSetter.delete(target);
    },
    disconnect: () => nextObserver.disconnect(),
  });
}
